import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Typography,
  Skeleton,
  Alert,
  AlertTitle,
  Tooltip,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";

const fieldTypes = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "textarea", label: "Textarea" },
];

const schema = yup.object({
  field_name: yup
    .string()
    .required("Field name required")
    .matches(/^[a-z0-9_]+$/, "lowercase alnum and underscores only"),
  field_label: yup.string().required("Field label required"),
  field_type: yup
    .string()
    .oneOf(fieldTypes.map((ft) => ft.value))
    .required("Field type required"),
  sort_order: yup.number().integer().required("Sort order required"),
  default_value: yup.string(),
  help_text: yup.string(),
  is_enabled: yup.boolean(),
  is_required: yup.boolean(),
  dropdown_options_text: yup.string(),
});

// Skeleton component for table rows
const TableRowSkeleton = () => (
  <>
    {Array.from(new Array(5)).map((_, index) => (
      <TableRow key={index}>
        <TableCell>
          <Skeleton variant="text" width="80%" height={30} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="70%" height={30} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="40%" height={30} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="50%" height={30} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="40%" height={30} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="20%" height={30} />
        </TableCell>
        <TableCell align="right">
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: "inline-block", mr: 1 }}
          />
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: "inline-block" }}
          />
        </TableCell>
      </TableRow>
    ))}
  </>
);

// Skeleton for protected fields banner
const ProtectedFieldsSkeleton = () => (
  <Box mb={2} p={2} sx={{ background: "#f5f5f5", border: "1px solid #e0e0e0" }}>
    <Skeleton variant="text" width="60%" height={25} />
  </Box>
);

export default function CustomFieldDefinitionManager() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSmall, setIsSmall] = useState(window.innerWidth < 600);
  const { domain } = useSelector((state) => state.tokenReducer);
  const [editing, setEditing] = useState(null);
  const [protectedFields, setProtectedFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [protectedFieldsLoading, setProtectedFieldsLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsSmall(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      field_name: "",
      field_label: "",
      field_type: "text",
      dropdown_options_text: "",
      default_value: "",
      is_enabled: true,
      is_required: false,
      sort_order: 0,
      help_text: "",
    },
  });

  const load = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        setProtectedFieldsLoading(true);

        const defs = await customGetAPI(
          `${domain}crm/custom-field-definitions/`,
          { signal }
        );
        const allFields = defs.responseDetails || [];
        const protectedFields = allFields.filter(
          (field) => field.is_used_in_scoring
        );

        // Update states
        setRows(allFields);
        setProtectedFields(protectedFields);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Load error:", e);
          alert("Failed to load field definitions");
        }
      } finally {
        setLoading(false);
        setProtectedFieldsLoading(false);
      }
    },
    [domain]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const onOpenNew = () => {
    setEditing(null);
    reset({
      field_name: "",
      field_label: "",
      field_type: "text",
      dropdown_options_text: "",
      default_value: "",
      is_enabled: true,
      is_required: false,
      sort_order: rows.length || 0,
      help_text: "",
    });
    setOpen(true);
  };

  const onEdit = (r) => {
    setEditing(r);
    reset({
      ...r,
      dropdown_options_text: Array.isArray(r.dropdown_options)
        ? r.dropdown_options.join(", ")
        : "",
    });
    setOpen(true);
  };

  const onDelete = async (r) => {
    if (protectedFields.some((pf) => pf.field_name === r.field_name)) {
      alert("Field in use. Remove from rules first.");
      return;
    }

    if (!window.confirm(`Delete ${r.field_label}?`)) return;

    try {
      setLoading(true);
      await customAPI(
        `${domain}crm/custom-field-definitions/${r.id}/`,
        "DELETE"
      );
      await load();
    } catch (e) {
      console.error("Delete error:", e);
      alert("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (vals) => {
    try {
      setLoading(true);
      const payload = {
        ...vals,
        dropdown_options:
          vals.field_type === "dropdown" && vals.dropdown_options_text
            ? vals.dropdown_options_text
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
      };

      delete payload.dropdown_options_text;

      console.log("Submitting payload:", payload);

      if (editing) {
        await customAPI(
          `${domain}crm/custom-field-definitions/${editing.id}/`,
          "PUT",
          payload
        );
      } else {
        await customAPI(
          `${domain}crm/custom-field-definitions/`,
          "POST",
          payload
        );
      }

      setOpen(false);
      await load();
    } catch (e) {
      console.error("Submit error:", e);
      alert("Save failed: " + (e.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const watchingType = watch("field_type");
  const onClose = () => setOpen(false);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <div>
          <h3>Custom Field Definitions</h3>
          <div style={{ color: "#666" }}>
            Define global custom fields for leads
          </div>
        </div>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onOpenNew}
          disabled={loading}
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
          }}
        >
          Add Custom Field
        </Button>
      </Box>

      {/* Protected Fields Skeleton */}
      {protectedFieldsLoading ? (
        <ProtectedFieldsSkeleton />
      ) : protectedFields.length > 0 ? (
        // <Box
        //   mb={2}
        //   p={2}
        //   sx={{ background: "#fff4e6", border: "1px solid #ffd8a8" }}
        // >
        //   Fields used in scoring rules can not be deleted:{" "}
        //   {protectedFields.map((pf) => pf.field_label).join(", ")}
        // </Box>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 2,
            backgroundColor: "#fff4e6",
            borderColor: "#ffd8a8",
            "& .MuiAlert-icon": {
              color: "#f76707",
            },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Fields in Use</AlertTitle>
          Fields used in scoring rules cannot be deleted:{" "}
          {protectedFields.map((pf) => pf.field_label).join(", ")}
        </Alert>
      ) : null}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Field Name</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton />
            ) : (
              <>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <code
                        style={{ background: "#f2f2f2", padding: "2px 6px" }}
                      >
                        {r.field_name}
                      </code>
                      {protectedFields.some(
                        (pf) => pf.field_name === r.field_name
                      ) && (
                        <Tooltip title="This field is used in scoring rules and cannot be deleted">
                          <WarningAmberIcon
                            sx={{
                              fontSize: 18,
                              color: "#f76707",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{r.field_label}</TableCell>
                    <TableCell>{r.field_type}</TableCell>
                    <TableCell>
                      {r.is_required ? "Required" : "Optional"}
                    </TableCell>
                    <TableCell>
                      {r.is_enabled ? "Enabled" : "Disabled"}
                    </TableCell>
                    <TableCell>{r.sort_order}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => onEdit(r)} disabled={loading}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => onDelete(r)}
                        disabled={
                          protectedFields.some(
                            (pf) => pf.field_name === r.field_name
                          ) || loading
                        }
                      >
                        <Delete
                          color={
                            protectedFields.some(
                              (pf) => pf.field_name === r.field_name
                            ) || loading
                              ? "disabled"
                              : "error"
                          }
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No custom fields defined
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && !loading) {
            onClose(event, reason);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: "hidden",
            width: isSmall ? "95%" : "auto",
            m: isSmall ? 1 : "auto",
          },
        }}
      >
        {/* Header with proper close button placement */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: isSmall ? "1.1rem" : "1.25rem",
            borderBottom: "1px solid #e0e0e0",
            pb: 2,
            pr: 2,
          }}
        >
          {editing ? "Edit Custom Field" : "Create Custom Field"}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ marginLeft: "auto" }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Form Content */}
        <DialogContent sx={{ pt: 2, pb: 0 }}>
          {loading ? (
            // Skeleton for form loading state
            <Box>
              <Grid container spacing={isSmall ? 1.5 : 2}>
                <Grid item xs={12} sm={6}>
                  <Skeleton
                    variant="text"
                    height={20}
                    width="40%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton
                    variant="text"
                    height={20}
                    width="40%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton
                    variant="text"
                    height={20}
                    width="40%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton
                    variant="text"
                    height={20}
                    width="40%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton
                    variant="text"
                    height={20}
                    width="40%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton
                    variant="text"
                    height={20}
                    width="40%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={60} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Skeleton variant="rectangular" width={120} height={20} />
                    <Skeleton variant="rectangular" width={120} height={20} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              id="custom-field-form"
            >
              <Grid container spacing={isSmall ? 1.5 : 2}>
                {/* Field Name */}
                {/* Field Name with custom styling */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Field Name *
                    <Typography
                      component="span"
                      sx={{
                        ml: 0.5,
                        color: "text.secondary",
                        fontWeight: 400,
                        fontSize: "0.8rem",
                      }}
                    >
                      (lowercase, no spaces)
                    </Typography>
                  </Typography>
                  <Controller
                    name="field_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., budget, decision_maker"
                        fullWidth
                        size="small"
                        error={!!errors.field_name}
                        helperText={errors.field_name?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Field Label with custom styling */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Field Label *
                  </Typography>
                  <Controller
                    name="field_label"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., Budget, Decision Maker"
                        fullWidth
                        size="small"
                        error={!!errors.field_label}
                        helperText={errors.field_label?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Field Type with custom styling */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Field Type *
                  </Typography>
                  <Controller
                    name="field_type"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        error={!!errors.field_type}
                        helperText={errors.field_type?.message}
                      >
                        {fieldTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Sort Order with custom styling */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Sort Order
                  </Typography>
                  <Controller
                    name="sort_order"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        placeholder="0"
                        fullWidth
                        size="small"
                        error={!!errors.sort_order}
                        helperText={errors.sort_order?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Dropdown Options (conditionally shown) */}
                {watchingType === "dropdown" && (
                  <Grid item xs={12}>
                    <Controller
                      name="dropdown_options_text"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Dropdown Options (comma separated)"
                          placeholder="Option 1, Option 2, Option 3"
                          fullWidth
                          size="small"
                          helperText="Enter options separated by commas"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Default Value */}
                <Grid item xs={12}>
                  <Controller
                    name="default_value"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Default Value (Optional)"
                        placeholder="Default value for this field"
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>

                {/* Help Text */}
                <Grid item xs={12}>
                  <Controller
                    name="help_text"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Help Text (Optional)"
                        placeholder="Helper text to show below the field"
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={loading}
                      />
                    )}
                  />
                </Grid>

                {/* Checkbox Options */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isSmall ? "column" : "row",
                      gap: isSmall ? 0.5 : 3,
                      alignItems: isSmall ? "flex-start" : "center",
                      pl: 0.5,
                    }}
                  >
                    <Controller
                      name="is_enabled"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              disabled={loading}
                            />
                          }
                          label="Enabled (show in forms)"
                        />
                      )}
                    />

                    <Controller
                      name="is_required"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              disabled={loading}
                            />
                          }
                          label="Required field"
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            pr: 3,
            pb: isSmall ? 1.5 : 2,
            pt: 1,
            flexDirection: isSmall ? "column-reverse" : "row",
            gap: isSmall ? 1 : 0,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth={isSmall}
            size="medium"
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            form="custom-field-form"
            variant="contained"
            size="medium"
            fullWidth={isSmall}
            disabled={loading}
            sx={{
              backgroundColor: "#84DEB8",
              color: "#000",
              "&:hover": { backgroundColor: "#6cc9a1" },
            }}
          >
            {loading ? "Saving..." : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
